// src/pages/PaymentsPage.js
import React, { useEffect, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  Divider,
  alpha
} from '@mui/material';
import {
  AccountBalance,
  Payment as PaymentIcon,
  Send,
  CallReceived,
  SwapHoriz,
  Receipt,
  ArrowBack,
  Logout,
  CheckCircle,
  Error,
  AccessTime
} from '@mui/icons-material';

const PaymentsPage = () => {
  const { user, logout } = useContext(AuthContext);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const navigate = useNavigate();

  // Transfer form state
  const [transferForm, setTransferForm] = useState({
    fromAccountId: '',
    toAccountId: '',
    amount: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchAccounts();
  }, [user, navigate]);

  useEffect(() => {
    if (selectedAccount) {
      fetchTransactions(selectedAccount._id);
    }
  }, [selectedAccount]);

  const fetchAccounts = async () => {
    try {
      const { data } = await API.get('/accounts');
      setAccounts(data.accounts || []);
      if (data.accounts?.length > 0) {
        setSelectedAccount(data.accounts[0]);
      }
    } catch (err) {
      showSnackbar('Failed to fetch accounts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (accountId) => {
    try {
      const { data } = await API.get(`/transactions/${accountId}`);
      setTransactions(prev => ({
        ...prev,
        [accountId]: data.history || []
      }));
    } catch (err) {
      console.error('Failed to fetch transactions', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'success': return <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />;
      case 'pending': return <AccessTime sx={{ fontSize: 16, color: '#FFB547' }} />;
      case 'failed': return <Error sx={{ fontSize: 16, color: '#EF4444' }} />;
      default: return null;
    }
  };

  // Handle Transfer
  const handleTransfer = async () => {
    // Validate
    const errors = {};
    if (!transferForm.fromAccountId) errors.fromAccountId = 'Select source account';
    if (!transferForm.toAccountId) errors.toAccountId = 'Select destination account';
    if (!transferForm.amount) errors.amount = 'Enter amount';
    if (transferForm.amount && parseFloat(transferForm.amount) <= 0) {
      errors.amount = 'Amount must be greater than 0';
    }

    // Check if same account
    if (transferForm.fromAccountId === transferForm.toAccountId) {
      errors.toAccountId = 'Cannot transfer to same account';
    }

    // Check balance
    const fromAccount = accounts.find(a => a._id === transferForm.fromAccountId);
    if (fromAccount && parseFloat(transferForm.amount) > fromAccount.balance) {
      errors.amount = 'Insufficient balance';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      await API.post('/transactions', {
        type: 'transfer',
        fromAccountId: transferForm.fromAccountId,
        toAccountId: transferForm.toAccountId,
        amount: parseFloat(transferForm.amount),
        description: transferForm.description || 'Transfer'
      });

      // Refresh accounts and transactions
      await fetchAccounts();
      if (selectedAccount) {
        await fetchTransactions(selectedAccount._id);
      }

      setOpenTransferDialog(false);
      setTransferForm({
        fromAccountId: '',
        toAccountId: '',
        amount: '',
        description: ''
      });
      setFormErrors({});
      showSnackbar('Transfer completed successfully');

    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Transfer failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const currentTransactions = selectedAccount ? transactions[selectedAccount._id] || [] : [];

  if (loading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button onClick={() => navigate('/dashboard')}><ArrowBack /></Button>
          <Typography variant="h6">Payments</Typography>
        </Box>
        <Button onClick={handleLogout}><Logout /></Button>
      </Paper>

      {/* Account Selector */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'grey.200', bgcolor: 'white' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Select Account
        </Typography>
        <Grid container spacing={1}>
          {accounts.map(account => (
            <Grid item xs={4} key={account._id}>
              <Paper
                onClick={() => setSelectedAccount(account)}
                sx={{
                  p: 1.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: selectedAccount?._id === account._id ? alpha('#3FCFA1', 0.1) : 'white',
                  border: '1px solid',
                  borderColor: selectedAccount?._id === account._id ? '#3FCFA1' : '#E2E8F0',
                  borderRadius: 2
                }}
              >
                <Typography variant="caption" sx={{ textTransform: 'capitalize', display: 'block' }}>
                  {account.type}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatCurrency(account.balance)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid', borderColor: 'grey.200' }}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Send />}
              onClick={() => setOpenTransferDialog(true)}
              sx={{ 
                py: 1.5,
                borderColor: '#E2E8F0',
                color: '#1A2B3C',
                '&:hover': { borderColor: '#3FCFA1' }
              }}
            >
              Transfer
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CallReceived />}
              disabled
              sx={{ py: 1.5, borderColor: '#E2E8F0', color: '#94A3B8' }}
            >
              Deposit
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SwapHoriz />}
              disabled
              sx={{ py: 1.5, borderColor: '#E2E8F0', color: '#94A3B8' }}
            >
              External
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Transactions */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Transaction History
          </Typography>
          <Chip 
            label={`${currentTransactions.length} transactions`}
            size="small"
            sx={{ bgcolor: alpha('#3FCFA1', 0.1), color: '#3FCFA1' }}
          />
        </Box>

        {currentTransactions.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <PaymentIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 2 }} />
            <Typography color="text.secondary">No transactions yet</Typography>
            <Button
              variant="contained"
              onClick={() => setOpenTransferDialog(true)}
              sx={{ mt: 2, bgcolor: '#3FCFA1', '&:hover': { bgcolor: '#2DAF81' } }}
            >
              Make a Transfer
            </Button>
          </Paper>
        ) : (
          <List>
            {currentTransactions.map((tx, index) => (
              <Card key={tx.id} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ 
                        bgcolor: tx.direction === 'inflow' ? alpha('#10B981', 0.1) : alpha('#EF4444', 0.1),
                        color: tx.direction === 'inflow' ? '#10B981' : '#EF4444'
                      }}>
                        {tx.direction === 'inflow' ? <CallReceived /> : <Send />}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {tx.description || (tx.type === 'transfer' ? 'Transfer' : tx.type)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(tx.createdAt)}
                          </Typography>
                          {getStatusIcon(tx.status)}
                        </Box>
                      </Box>
                    </Box>
                    <Typography 
                      variant="body1" 
                      fontWeight="bold"
                      sx={{ color: tx.direction === 'inflow' ? '#10B981' : '#EF4444' }}
                    >
                      {tx.direction === 'inflow' ? '+' : '-'}{formatCurrency(tx.amount)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </List>
        )}
      </Box>

      {/* Transfer Dialog */}
      <Dialog 
        open={openTransferDialog} 
        onClose={() => setOpenTransferDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">Transfer Money</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              select
              fullWidth
              label="From Account"
              value={transferForm.fromAccountId}
              onChange={(e) => setTransferForm({ ...transferForm, fromAccountId: e.target.value })}
              error={!!formErrors.fromAccountId}
              helperText={formErrors.fromAccountId}
              margin="normal"
              size="small"
            >
              {accounts.map(account => (
                <MenuItem key={account._id} value={account._id}>
                  {account.type.charAt(0).toUpperCase() + account.type.slice(1)} - {formatCurrency(account.balance)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label="To Account"
              value={transferForm.toAccountId}
              onChange={(e) => setTransferForm({ ...transferForm, toAccountId: e.target.value })}
              error={!!formErrors.toAccountId}
              helperText={formErrors.toAccountId}
              margin="normal"
              size="small"
            >
              {accounts.map(account => (
                <MenuItem key={account._id} value={account._id}>
                  {account.type.charAt(0).toUpperCase() + account.type.slice(1)} - {formatCurrency(account.balance)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={transferForm.amount}
              onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
              error={!!formErrors.amount}
              helperText={formErrors.amount}
              margin="normal"
              size="small"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1, color: '#64748B' }}>$</Typography>
              }}
            />

            <TextField
              fullWidth
              label="Description (Optional)"
              value={transferForm.description}
              onChange={(e) => setTransferForm({ ...transferForm, description: e.target.value })}
              margin="normal"
              size="small"
              placeholder="What's this for?"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenTransferDialog(false)} sx={{ color: '#64748B' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleTransfer}
            disabled={submitting}
            sx={{ bgcolor: '#3FCFA1', '&:hover': { bgcolor: '#2DAF81' } }}
          >
            {submitting ? 'Processing...' : 'Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentsPage;